import teamJobsConfig from '@/configs/permissions/team-jobs.json';
import type { Requirement } from '@/lib/capability/requirement';
import type { TeamRole } from '@/lib/team/permissions';
import { getSystemTemplate, getTemplateDefaultRole } from './team-templates';

// The Team page's short job list (doc 09 §3). Edit the JSON, not this file
// (RULES §15). Each job points at a system template; the server derives role
// and permissions from that template, so this list only decides what the
// owner is OFFERED, never what anyone is granted.

export type TeamJobAccess = 'pin' | 'invite';

export interface TeamJob {
  id: string;
  templateCode: string;
  label: string;
  blurb: string;
  access: TeamJobAccess;
  /** How an invite reaches them. Only for `access: 'invite'`. */
  identity?: 'email' | 'phone';
  need: Requirement;
}

/** Template codes a till operator (PIN, no sign-in) may be created from. */
export const PIN_JOB_TEMPLATE_CODES = ['counter_staff', 'front_of_house', 'kitchen'] as const;
export type PinJobTemplateCode = (typeof PIN_JOB_TEMPLATE_CODES)[number];

let jobs: readonly TeamJob[] | null = null;

// Built on first read, not at import: index.ts re-exports both this module and
// team-templates.ts, and resolving templates at import time races that cycle.
function loadJobs(): readonly TeamJob[] {
  // A job whose template disappeared would make every create fail server-side.
  // Drop it here; team-jobs.test.ts fails loudly so it never ships that way.
  jobs ??= (teamJobsConfig.jobs as TeamJob[]).filter((job) => Boolean(getSystemTemplate(job.templateCode)));
  return jobs;
}

export function getTeamJobs(): readonly TeamJob[] {
  return loadJobs();
}

export function getTeamJobByTemplate(templateCode: string | null | undefined): TeamJob | undefined {
  return loadJobs().find((job) => job.templateCode === templateCode);
}

/**
 * The job a stored role reads as, e.g. `pos_counter_operator` → "Cashier".
 * Members store a role, not the job they were added as, so the list shows the
 * first job whose template defaults to that role. Undefined for roles with no
 * job here (Supervisor, Viewer, …): callers fall back to the role label.
 */
export function getTeamJobForRole(role: TeamRole): TeamJob | undefined {
  return loadJobs().find((job) => getTemplateDefaultRole(job.templateCode) === role);
}
